import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { UFS, SERVICOS, formatWhatsapp } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl bg-white border border-slate-300 px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2E9FDB]/60 focus:ring-1 focus:ring-[#2E9FDB]/40 transition-colors appearance-none";

export const LeadForm = () => {
  const [form, setForm] = useState({ uf: "", cidade: "", servico: "", nome: "", whatsapp: "", email: "", consentimento: false });
  const [cidades, setCidades] = useState([]);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!form.uf) return;
    setLoadingCidades(true);
    setCidades([]);
    axios
      .get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.uf}/municipios?orderBy=nome`)
      .then((res) => setCidades(res.data.map((c) => c.nome)))
      .catch(() => toast.error("Não foi possível carregar as cidades. Tente novamente."))
      .finally(() => setLoadingCidades(false));
  }, [form.uf]);

  const set = (field) => (e) => {
    const value = field === "whatsapp" ? formatWhatsapp(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [field]: value, ...(field === "uf" ? { cidade: "" } : {}) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consentimento) {
      toast.error("É necessário autorizar o contato para enviar a solicitação.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
    } catch {
      toast.error("Não foi possível enviar sua solicitação. Verifique os dados e tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="solicitar" data-testid="lead-form-section" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="success-message"
          className="max-w-xl mx-auto rounded-3xl bg-white border border-[#2E9FDB]/25 shadow-sm p-10 md:p-14 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-[#2E9FDB]/10 border border-[#2E9FDB]/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#2E9FDB]" />
          </div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 mb-4">Solicitação recebida.</h2>
          <p className="text-sm md:text-base text-slate-600 leading-relaxed">
            Recebemos sua solicitação. O setor responsável entrará em contato para dar
            continuidade ao atendimento.
          </p>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="solicitar" data-testid="lead-form-section" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <p className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#2E9FDB] mb-4">Atendimento</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900">Solicite atendimento</h2>
          <p className="text-sm text-slate-600 mt-4 max-w-md mx-auto">
            Preencha os dados abaixo e direcionaremos sua solicitação ao profissional adequado.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          data-testid="lead-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-10 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">UF</label>
              <select data-testid="input-uf" required value={form.uf} onChange={set("uf")} className={inputCls}>
                <option value="">Selecione</option>
                {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">Cidade</label>
              <select data-testid="input-cidade" required value={form.cidade} onChange={set("cidade")} disabled={!form.uf || loadingCidades} className={`${inputCls} disabled:opacity-50`}>
                <option value="">{loadingCidades ? "Carregando cidades..." : form.uf ? "Selecione a cidade" : "Selecione o estado primeiro"}</option>
                {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">Serviço desejado</label>
            <select data-testid="input-servico" required value={form.servico} onChange={set("servico")} className={inputCls}>
              <option value="">Selecione o serviço</option>
              {SERVICOS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">Nome</label>
            <input data-testid="input-nome" required type="text" placeholder="Seu nome completo" value={form.nome} onChange={set("nome")} className={inputCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">WhatsApp</label>
              <input data-testid="input-whatsapp" required type="tel" placeholder="(00) 00000-0000" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-heading font-semibold uppercase tracking-wide text-slate-600 mb-2">E-mail</label>
              <input data-testid="input-email" required type="email" placeholder="seuemail@exemplo.com" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <input
              data-testid="input-consentimento"
              type="checkbox"
              checked={form.consentimento}
              onChange={(e) => setForm((f) => ({ ...f, consentimento: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 bg-white accent-[#2E9FDB]"
            />
            <span className="text-xs text-slate-600 leading-relaxed">
              Autorizo o contato para atendimento da solicitação realizada. Autorizo o uso dos
              dados informados para o atendimento da solicitação.
            </span>
          </label>

          <button
            data-testid="submit-lead-button"
            type="submit"
            disabled={submitting}
            className="btn-cta w-full bg-[#2E9FDB] text-[#06305F] font-heading font-bold text-sm sm:text-base py-4 rounded-full uppercase tracking-wide disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Enviando..." : "Solicitar atendimento"}
          </button>

          <p className="text-[11px] text-slate-500 leading-relaxed text-center pt-1 flex items-center justify-center gap-1.5 flex-wrap">
            <ShieldCheck className="w-3.5 h-3.5 text-[#2E9FDB]" />
            Seus dados são utilizados exclusivamente para atendimento desta solicitação.{" "}
            <Link to="/privacidade" data-testid="privacy-link-form" className="underline text-slate-600 hover:text-[#2E9FDB] transition-colors">
              Política de Privacidade
            </Link>
          </p>
        </motion.form>
      </div>
    </section>
  );
};
